import Papa from 'papaparse';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { Box, Tab, Grid, Tabs, Stack, Backdrop, Typography } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import { useModal } from 'src/hooks/useModal';
import { usePermission } from 'src/hooks/usePermission';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { getComparator } from 'src/utils/filterData';

import authService from 'src/services/auth.service';
import { genericApiToaster } from 'src/services/utils';
import { fetchAllPermissions } from 'src/services/role.service';
import { fetchDepartmentsSummary } from 'src/services/department.service';
import { titleHeadingStyle, titleHeadingStyleSmole } from 'src/pages/style';
import {
  createUser,
  updateUser,
  uploadUsers,
  getUserAvatarUrl,
  getUserPermissions,
  grantUserPermission,
  revokeUserPermission,
  getLightweightUsersByDepartments,
} from 'src/services/user.service';

import Loader from 'src/components/loaders/Loader';
import CSVInput from 'src/components/inputs/CSVInput';
import ActionButton from 'src/components/action-button/action-button';

import AddUserForm from 'src/sections/users/modals/AddUserForm';
import ModifyUserModal from 'src/sections/users/modals/ModifyUserModal';

import UsersTable from './table/table';
import UserPermissionEditor from './modals/UserPermissionEditor';

UsersView.propTypes = {
  title: PropTypes.string,
};

export default function UsersView({ title }) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const auth = useAuth();
  const { showToast, hideToast } = useToast();
  const { showModal, hideModal } = useModal();
  const { can } = usePermission();

  const [usersData, setUsersData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openModifyUserModal, setModifyUserModal] = useState(false);
  const [page, setPage] = useState(0);
  const [departmentsData, setDepartmentData] = useState([]);
  const [order, setOrder] = useState('asc');
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [searchBy, setSearchBy] = useState('fullName');
  const [searchValue, setSearchValue] = useState('');
  const [filterBy, setFilterBy] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [dataInCSV, setDataInCSV] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditingPermissions, setIsEditingPermissions] = useState(false);
  const [userPerms, setUserPerms] = useState([]);
  const [systemPerms, setSystemPerms] = useState([]);
  const [isDownloadDebouncing, setIsDownloadDebouncing] = useState(false);

  const lastSyncRef = useRef('');
  const departmentIds = useMemo(() => auth?.departmentIds || [], [auth?.departmentIds]);

  const canCreateUser = can({ permission: 'USER:CREATE' });
  const canUpdateUser = can({ permission: 'USER:UPDATE' });
  const canDeleteUser = can({ permission: 'USER:DELETE' });
  const canUploadUsers = can({ permission: 'CSV:UPLOAD' });
  const canDownloadUsers = can({ permission: 'CSV:DOWNLOAD' });
  const canManagePermissions = can({ permission: 'PERMISSIONS:UPDATE' });

  const userCapabilities = useMemo(
    () => ({
      canCreateUser,
      canUpdateUser,
      canDeleteUser,
      canUploadUsers,
      canDownloadUsers,
      canManagePermissions,
    }),
    [
      canCreateUser,
      canUpdateUser,
      canDeleteUser,
      canUploadUsers,
      canDownloadUsers,
      canManagePermissions,
    ]
  );

  const fetchUsersSuccessCallbak = useCallback(
    (res) => {
      const data = Array.isArray(res) ? res : [];

      const filteredByArchiveState = data.filter((user) =>
        activeTab === 0 ? !user.archived : user.archived
      );

      setUsersData(
        filteredByArchiveState.map((user) => ({
          ...user,
          fullName: `${user.givenName} ${user.familyName}`,
          status: user.status ? 'true' : 'false',
          avatarUrl: user.avatarVersion
            ? getUserAvatarUrl(user.id, user.avatarVersion)
            : null,
        }))
      );
    },
    [activeTab]
  );

  const fetchUsersData = useCallback(
    (force = false) => {
      const currentSyncKey = JSON.stringify(departmentIds) + activeTab;
      if (!force && currentSyncKey === lastSyncRef.current) return;
      if (departmentIds.length === 0) {
        setIsLoading(false);
        return;
      }

      lastSyncRef.current = currentSyncKey;
      setIsLoading(true);

      genericApiToaster(
        getLightweightUsersByDepartments,
        [departmentIds, activeTab],
        '',
        t('errorMessages.couldntLoadUser'),
        showToast,
        hideToast,
        t,
        fetchUsersSuccessCallbak,
        undefined,
        () => setIsLoading(false)
      );
    },
    [departmentIds, activeTab, t, showToast, hideToast, fetchUsersSuccessCallbak]
  );

  const fetchDepartmentsData = useCallback(() => {
    genericApiToaster(
      fetchDepartmentsSummary,
      [],
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      setDepartmentData
    );
  }, [t, showToast, hideToast]);

  useEffect(() => {
    fetchDepartmentsData();
  }, [fetchDepartmentsData]);

  useEffect(() => {
    fetchUsersData();
  }, [fetchUsersData, activeTab]);

  const handleDownloadClick = () => {
    if (!isDownloadDebouncing) {
      setIsDownloadDebouncing(true);
      setTimeout(() => setIsDownloadDebouncing(false), 1500);
    }
  };

  const filteredUsers = useMemo(() => {
    const deptMap = Object.fromEntries(
      (departmentsData || []).map((d) => [d.id, d.name])
    );

    // 1. Resolve and prepare all searchable fields dynamically with safe fallbacks
    const resolvedUsers = (usersData || []).map((user) => {
      const resolvedDepts = (user.departmentIds || [])
        .map((id) => deptMap[id])
        .filter(Boolean);
      return {
        ...user,
        fullName: `${user.givenName || ''} ${user.familyName || ''}`.trim(),
        departmentName: resolvedDepts.join(', '),
        departmentNames: resolvedDepts,
        roleDisplayName: t(`roles.${user.role}`),
      };
    });

    let result = resolvedUsers;

    // First: Apply categorical dropdown filters (Role, Department, Status)
    if (filterBy && filterValue) {
      const target = filterValue.toLowerCase().trim();
      result = result.filter((u) => {
        if (filterBy === 'status') return String(u.status) === target;
        if (filterBy === 'departmentName')
          return u.departmentName.toLowerCase().includes(target);
        if (filterBy === 'role') return u.role === filterValue;
        return true;
      });
    }

    // Second: Apply text query search (ID, Name, Email)
    if (searchValue) {
      const query = searchValue.toLowerCase().trim();
      result = result.filter((u) => {
        if (searchBy === 'id') return String(u.id).includes(query);
        if (searchBy === 'fullName') return u.fullName.toLowerCase().includes(query);
        if (searchBy === 'email') return u.email && u.email.toLowerCase().includes(query);
        return true;
      });
    }

    // Third: Sort the final filtered array, mapping the undefined 'name' target to 'fullName' safely
    const comparator = getComparator(order, orderBy === 'name' ? 'fullName' : orderBy);
    const stabilizedThis = result.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderCompare = comparator(a[0], b[0]);
      if (orderCompare !== 0) return orderCompare;
      return a[1] - b[1];
    });

    return stabilizedThis.map((el) => el[0]);
  }, [
    searchValue,
    order,
    orderBy,
    searchBy,
    filterBy,
    filterValue,
    usersData,
    departmentsData,
    t,
  ]);

  useEffect(() => {
    setDataInCSV(Papa.unparse(filteredUsers));
  }, [filteredUsers]);

  const addNewUser = async (newUser) => {
    if (canCreateUser) {
      handleBackdropOpen();
      genericApiToaster(
        createUser,
        { ...newUser, username: `${newUser.givenName} ${newUser.familyName}` },
        t('successMessages.successUserAdded'),
        t('errorMessages.couldntCreateUser'),
        showToast,
        hideToast,
        t,
        () => fetchUsersData(true),
        undefined,
        handleBackdropClose
      );
    }
  };

  const modifyUser = async (newUserData) => {
    genericApiToaster(
      updateUser,
      [newUserData.id, newUserData],
      t('successMessages.successUserUpdate'),
      t('errorMessages.couldntUpdateUser'),
      showToast,
      hideToast,
      t,
      () => fetchUsersData(true)
    );
  };

  const handleArchiveUser = async (userRow) => {
    const archivedPayload = {
      id: userRow.id,
      givenName: userRow.givenName,
      familyName: userRow.familyName,
      email: userRow.email,
      phoneNumber: userRow.phoneNumber,
      departmentIds: userRow.departmentIds,
      role: userRow.role,
      isArchived: true,
      status: false,
    };

    genericApiToaster(
      updateUser,
      [userRow.id, archivedPayload],
      t('successMessages.successUserArchive') || 'User archived successfully',
      t('errorMessages.couldntUpdateUser'),
      showToast,
      hideToast,
      t,
      () => fetchUsersData(true)
    );
  };

  const handleRestoreUser = async (userRow) => {
    const activePayload = {
      id: userRow.id,
      givenName: userRow.givenName,
      familyName: userRow.familyName,
      email: userRow.email,
      phoneNumber: userRow.phoneNumber,
      departmentIds: userRow.departmentIds,
      role: userRow.role,
      isArchived: false,
      status: true,
    };

    genericApiToaster(
      updateUser,
      [userRow.id, activePayload],
      t('successMessages.successUserRestore') || 'User restored successfully',
      t('errorMessages.couldntUpdateUser'),
      showToast,
      hideToast,
      t,
      () => fetchUsersData(true)
    );
  };

  const csvUpload = (formData) => {
    if (canUploadUsers) {
      genericApiToaster(
        uploadUsers,
        formData,
        t('successMessages.successUsersUpload'),
        t('errorMessages.errorFileUpload'),
        showToast,
        hideToast,
        t,
        () => fetchUsersData(true),
        undefined,
        handleBackdropClose
      );
    }
  };

  const handleBackdropClose = () => setBackdropOpen(false);
  const handleBackdropOpen = () => setBackdropOpen(true);
  const handleTabChange = (event, newTab) => {
    setActiveTab(newTab);
    setPage(0);
  };

  const handleEditPermissions = async (userRow) => {
    setSelectedRow(userRow);
    try {
      // 1. Fetch user-specific overrides directly from the lightweight DTO
      const res = await getUserPermissions(userRow.id);
      setUserPerms(res.permissions || []);

      // 2. Fetch all system permissions on demand (if they aren't already loaded)
      if (systemPerms.length === 0) {
        const allPerms = await fetchAllPermissions();
        setSystemPerms(allPerms);
      }

      setIsEditingPermissions(true);
    } catch (e) {
      console.error('Failed to load permission edit context:', e);
    }
  };

  const handleToggleUserPermission = async (permKey) => {
    const isAdding = !userPerms.includes(permKey);
    const permEntity = systemPerms.find((p) => p.permissionName === permKey);
    if (!permEntity) return;
    try {
      if (isAdding) await grantUserPermission(selectedRow.id, permEntity.id);
      else await revokeUserPermission(selectedRow.id, permEntity.id);
      setUserPerms((prev) =>
        isAdding ? [...new Set([...prev, permKey])] : prev.filter((p) => p !== permKey)
      );
    } catch (e) {
      console.error(e);
    }
  };

  if (isEditingPermissions) {
    return (
      <UserPermissionEditor
        user={selectedRow}
        userPerms={userPerms}
        onToggle={handleToggleUserPermission}
        onCancel={(isSaved) => {
          setIsEditingPermissions(false);
          if (isSaved === true) {
            showToast({
              message: t('rolesPage.permissionSyncSuccess'),
              severity: 'success',
              props: { hideToast },
            });
          }
        }}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title> {t(`nav.${title}`)} </title>
      </Helmet>
      {selectedRow && (
        <ModifyUserModal
          open={openModifyUserModal}
          setOpen={setModifyUserModal}
          row={selectedRow}
          modifyUser={modifyUser}
          departmentsData={departmentsData}
          currentRole={authService.verifyUserRole()}
        />
      )}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={`${t('userStatus.active')} ${t(`nav.${title}`)}`} />
          <Tab label={`${t('userStatus.archived')} ${t(`nav.${title}`)}`} />
        </Tabs>
      </Box>
      <Box px={3} mt={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography style={isSmallScreen ? titleHeadingStyleSmole : titleHeadingStyle}>
            {activeTab === 0 ? t('userStatus.active') : t('userStatus.archived')}{' '}
            {t(`nav.${title}`)}
          </Typography>
          <Grid>
            {canCreateUser && (
              <ActionButton
                onClick={() =>
                  showModal({
                    title: t('pages.usersPage.form.newUser'),
                    Component: AddUserForm,
                    props: {
                      addUser: addNewUser,
                      hideModal,
                      departmentsData,
                      currentRole: authService.verifyUserRole(),
                    },
                  })
                }
                text={t('buttons.newUser')}
                icon="eva:plus-fill"
                sx={{ mr: 1 }}
              />
            )}

            {canDownloadUsers && (
              <ActionButton
                onClick={handleDownloadClick}
                href={`data:text/csv;charset=utf-8,${dataInCSV}`}
                download="users.csv"
                text={t('buttons.download')}
                icon="eva:download-fill"
                sx={{ mx: 1 }}
              />
            )}

            {canUploadUsers && (
              <ActionButton
                text={t('buttons.uploadUsers')}
                icon="eva:upload-fill"
                onClick={() =>
                  showModal({
                    title: t('inputs.CSV.uploadCSV'),
                    Component: CSVInput,
                    props: { hideModal, csvUpload, maxWidth: true },
                  })
                }
                sx={{ ml: 1 }}
              />
            )}
          </Grid>
        </Stack>
        <Backdrop
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(4px)',
            zIndex: 999,
          }}
          open={backdropOpen}
        >
          <Loader size={175} hideText />
        </Backdrop>
        <UsersTable
          filterName={searchValue}
          setFilterName={setSearchValue}
          searchBy={searchBy}
          setSearchBy={setSearchBy}
          filterBy={filterBy}
          setFilterBy={setFilterBy}
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          fullFilteredData={filteredUsers}
          role={auth?.role}
          setRowsPerPage={setRowsPerPage}
          selected={selected}
          setSelected={setSelected}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          rowsPerPage={rowsPerPage}
          page={page}
          setPage={setPage}
          fetchData={() => fetchUsersData(true)}
          title={title}
          departmentsData={departmentsData}
          setSelectedRow={setSelectedRow}
          selectedRow={selectedRow}
          setModifyUserModal={setModifyUserModal}
          isLoading={isLoading}
          activeTab={activeTab}
          usersData={usersData}
          onEditPermissions={handleEditPermissions}
          userCapabilities={userCapabilities}
          onArchive={handleArchiveUser}
          onRestore={handleRestoreUser}
          setOrderBy={setOrderBy}
        />
      </Box>
    </>
  );
}
